import * as axios from "axios";
import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import {
	OrganizationResponse,
	RoutingBase,
	SiteFabricRunStatus,
	UnitPreferenceSave,
} from "@/type/api";
import { useSessionStore } from "@/store/session";

// OrganizationSave is one auto-saved edit. Only the keys present are sent, so
// a change to one field never overwrites another; an explicit null clears a
// stored text value, and an omitted key is left alone.
export interface OrganizationSave {
	name?: string | null;
	website?: string | null;
	generalManagerName?: string | null;
	officeAddressStreet?: string | null;
	officeAddressCity?: string | null;
	officeAddressPostalCode?: string | null;
	officePhone?: string | null;
	officeFax?: string | null;
	officeEmail?: string | null;
	unitPreference?: Partial<UnitPreferenceSave>;
	// The district's drive-plan settings (§5 of the routing design): where the
	// trucks start and finish, which Valhalla costing profile the district
	// drives, and the minutes a stop of each kind is planned with. An explicit
	// null clears the base point; routingServiceMinutes is the whole map, so a
	// present key replaces every kind default at once.
	routingBase?: RoutingBase | null;
	routingCosting?: string;
	routingServiceMinutes?: Record<string, number>;
}

export const useStoreOrganization = defineStore("organization", () => {
	// State
	const settings = ref<OrganizationResponse | null>(null);
	const saving = ref(false);
	const loading = ref(false);
	const error = ref<string | null>(null);
	const siteFabric = ref<SiteFabricRunStatus | null>(null);

	// Computed / derived
	const serviceAreaSquareMeters = (): number | null => {
		return settings.value?.serviceArea?.areaSquareMeters ?? null;
	};

	// Actions
	async function fetchSettings(): Promise<OrganizationResponse> {
		const session = useSessionStore();
		const orgId = session.organization?.id;
		if (!orgId) {
			throw new Error("No organization ID available from session");
		}

		loading.value = true;
		error.value = null;
		try {
			const data: OrganizationResponse = await apiClient.JSONGet(
				`/api/organization/${orgId}`,
			);

			const normalized: OrganizationResponse = {
				id: data.id,
				name: data.name ?? "",
				active: data.active ?? true,
				website: data.website ?? null,
				generalManagerName: data.generalManagerName ?? null,
				officeAddressStreet: data.officeAddressStreet ?? null,
				officeAddressCity: data.officeAddressCity ?? null,
				officeAddressPostalCode: data.officeAddressPostalCode ?? null,
				officePhone: data.officePhone ?? null,
				officeEmail: data.officeEmail ?? null,
				officeFax: data.officeFax ?? null,
				serviceArea: data.serviceArea ?? undefined,
				satelliteSurveillanceArea: data.satelliteSurveillanceArea ?? undefined,
				unitPreference: data.unitPreference ?? {
					preset: "imperial",
					area: null,
					mass: null,
					volume: null,
					areaBasis: null,
					volumeBasis: null,
				},
				routingBase: data.routingBase ?? null,
				routingCosting: data.routingCosting ?? "auto",
				routingServiceMinutes: data.routingServiceMinutes ?? {},
			};
			settings.value = normalized;
			return normalized;
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Unknown error";
			error.value = msg;
			throw err;
		} finally {
			loading.value = false;
		}
	}

	async function saveChanges(data: OrganizationSave): Promise<void> {
		const session = useSessionStore();
		const orgId = session.organization?.id;
		if (!orgId) {
			throw new Error("No organization ID available from session");
		}

		saving.value = true;
		error.value = null;
		try {
			// An auto-save sends only the field it changed, so an absent key is
			// left untouched. An explicit null is a value in its own right — it
			// clears the stored text — so only undefined is dropped here.
			const body: Record<string, unknown> = {};
			for (const [key, value] of Object.entries(data)) {
				if (value !== undefined) {
					body[key] = value;
				}
			}

			await apiClient.JSONPut(`/api/organization/${orgId}`, body);

			// Deliberately no reload here: the page auto-saves one field at a
			// time, and re-reading the row after each save would reset the
			// fields the operator is still editing back to the server's older
			// values. The caller keeps the values it already holds.
		} catch (err: unknown) {
			let message = "Failed to save changes. Please try again.";
			if (err instanceof axios.AxiosError) {
				const data = err.response?.data;
				if (data && typeof data === "object" && "message" in data) {
					message = (data as { message: string }).message;
				} else if (data) {
					message = JSON.stringify(data);
				}
			}
			error.value = message;
			throw err;
		} finally {
			saving.value = false;
		}
	}

	// fetchSiteFabric loads the organization's most recent site-fabric run
	// (status, counties and verification report).
	async function fetchSiteFabric(): Promise<SiteFabricRunStatus> {
		const session = useSessionStore();
		const orgId = session.organization?.id;
		if (!orgId) {
			throw new Error("No organization ID available from session");
		}
		const data = await apiClient.JSONGet<SiteFabricRunStatus>(
			`/api/organization/${orgId}/site-fabric`,
		);
		siteFabric.value = data;
		return data;
	}

	// runSiteFabric was removed: starting a fabric run is a Gleipnir-operator
	// action and now lives on the sudo surface (POST /api/sudo/tiger/run,
	// ts/view/sudo/Tiger.vue). The district's Organization page keeps the
	// read-only status view above.

	return {
		// State
		settings,
		saving,
		loading,
		error,
		siteFabric,
		serviceAreaSquareMeters,
		// Actions
		fetchSettings,
		saveChanges,
		fetchSiteFabric,
	};
});
